import { type JSX } from 'preact';
import { Card, CardContent, CardHeader, CardTitle } from './Card';

export function PrivacyPolicy(): JSX.Element {
  return (
    <div className="max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Privacy Policy</CardTitle>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 transition-colors duration-200">Last updated: December 15, 2024</p>
        </CardHeader>
        <CardContent className="prose prose-sm max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 transition-colors duration-200">Our Commitment to Privacy</h2>
            <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-200">
              This service is built on a <strong>zero-knowledge architecture</strong>. We encrypt your
              secrets in your browser before sending them to our server. We never have access to your
              unencrypted secrets or decryption keys.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 transition-colors duration-200">What Data We Collect</h2>

            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4 mb-2 transition-colors duration-200">1. Encrypted Secrets</h3>
            <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-200">
              <strong>What:</strong> Your secret data, encrypted client-side using AES-GCM encryption
              <br />
              <strong>Purpose:</strong> Store and deliver your secret to the intended recipient
              <br />
              <strong>Retention:</strong> Automatically deleted immediately after retrieval or upon expiration
              <br />
              <strong>Access:</strong> We cannot decrypt or read your secrets (zero-knowledge)
            </p>

            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4 mb-2 transition-colors duration-200">2. Name and email address</h3>
            <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-200">
              When you sign in with OAuth (Google/Facebook/GitHub/Apple):
            </p>
            <div className="ml-4 mb-4">
              <p>
                <strong>For Burn Notifications (Optional):</strong>
                <br />
                • You can choose to receive notifications when your secrets are retrieved or expire
                <br />
                • We encrypt and temporarily store your email for this purpose
                <br />
                • Legal basis: Your explicit consent via checkbox (GDPR Art. 6(1)(a))
                <br />
                • Retention: Deleted automatically when the secret is processed
                <br />
                • You can disable this feature at any time
              </p>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4 mb-2 transition-colors duration-200">3. IP Addresses (Anonymized)</h3>
            <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-200">
              <strong>What:</strong> IP addresses with the last octet removed (e.g., 192.168.1.123 becomes 192.168.1.0)
              <br />
              <strong>Purpose:</strong>
            </p>
            <ul className="list-disc ml-6 mb-4">
              <li>Rate limiting to prevent abuse</li>
              <li>Security monitoring and threat detection</li>
              <li>Service reliability monitoring</li>
            </ul>
            <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-200">
              <strong>Status:</strong> Anonymized data that cannot identify you as an individual
              <br />
              <strong>Retention:</strong> Automatically deleted after 90 days
              <br />
              <strong>GDPR Status:</strong> Not considered personal data (anonymized)
            </p>

            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4 mb-2 transition-colors duration-200">4. Browser Information</h3>
            <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-200">
              <strong>What:</strong> User agent string (browser type and version)
              <br />
              <strong>Purpose:</strong> Service compatibility and security monitoring
              <br />
              <strong>Retention:</strong> Deleted with metrics after 90 days
            </p>

            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4 mb-2 transition-colors duration-200">5. Cookies</h3>
            <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-200">
              <strong>What:</strong> Session cookies for OAuth authentication
              <br />
              <strong>Purpose:</strong> Maintain your login session
              <br />
              <strong>Type:</strong> Strictly necessary (authentication)
              <br />
              <strong>Retention:</strong> Cleared when you log out or session expires
              <br />
              <strong>Third-party cookies:</strong> None (no tracking or advertising)
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 transition-colors duration-200">What We DO NOT Collect</h2>
            <ul className="list-disc ml-6 mb-4">
              <li>Decryption keys (stored only in URL fragments, never sent to server)</li>
              <li>Plaintext secrets (encrypted in your browser)</li>
              <li>Tracking cookies or analytics cookies</li>
              <li>Social media tracking pixels</li>
              <li>Advertising data</li>
              <li>Full IP addresses (only anonymized)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 transition-colors duration-200">Your Rights Under GDPR</h2>
            <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-200">
              If you are in the European Economic Area (EEA), you have the following rights:
            </p>

            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4 mb-2 transition-colors duration-200">Right to Access</h3>
            <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-200">
              Request a copy of the personal data we hold about you. You may access your data through "my secrets".
            </p>

            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4 mb-2 transition-colors duration-200">Right to Erasure</h3>
            <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-200">
              Request deletion of your personal data. You can delete your account and all
              associated data through "my secrets".
            </p>

            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4 mb-2 transition-colors duration-200">Right to Rectification</h3>
            <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-200">
              Correct any inaccurate personal data. Update your email through authentication provider settings (eg. Google).
            </p>

            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4 mb-2 transition-colors duration-200">Right to Data Portability</h3>
            <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-200">
              Receive your data in a structured, machine-readable format. You may access your data
              through "my secrets".
            </p>

            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4 mb-2 transition-colors duration-200">Right to Withdraw Consent</h3>
            <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-200">
              For burn notifications: Uncheck the notification option when creating secrets.
              <br />
              For authentication: Use our service anonymously and do not sign in.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 transition-colors duration-200">Data Retention</h2>
            <table className="min-w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-2 text-left">Data Type</th>
                  <th className="border border-gray-300 px-4 py-2 text-left">Retention Period</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">Encrypted secrets</td>
                  <td className="border border-gray-300 px-4 py-2">Deleted immediately on retrieval or expiration</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">Email for notifications</td>
                  <td className="border border-gray-300 px-4 py-2">Deleted when secret is processed</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">Anonymized metrics</td>
                  <td className="border border-gray-300 px-4 py-2">90 days, then auto-deleted</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">Session cookies</td>
                  <td className="border border-gray-300 px-4 py-2">Until logout or expiration</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 transition-colors duration-200">Security Measures</h2>
            <ul className="list-disc ml-6 mb-4">
              <li>Client-side encryption (AES-GCM) before data leaves your browser</li>
              <li>HTTPS/TLS encryption for all data in transit</li>
              <li>Decryption keys never transmitted to server (URL fragments)</li>
              <li>Encrypted email addresses in database</li>
              <li>Automatic deletion of secrets after retrieval</li>
              <li>Rate limiting to prevent abuse</li>
              <li>Regular security audits</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 transition-colors duration-200">Important Security Limitations</h2>
            <p className="mb-4 text-amber-800 bg-amber-50 border border-amber-200 rounded p-4">
              <strong>⚠️ TLS Interception:</strong> This service cannot protect against corporate
              HTTPS interception (SSL inspection). If your network uses SSL inspection, the decryption
              keys may be visible to network administrators. For maximum security, use a personal
              device on a personal network.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 transition-colors duration-200">Third-Party Services</h2>
            <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-200">
              We use the following third-party services:
            </p>

            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4 mb-2 transition-colors duration-200">OAuth Providers</h3>
            <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-200">
              Google, Facebook, GitHub, and Apple OAuth for authentication. Each provider has their own
              privacy policy:
            </p>
            <ul className="list-disc ml-6 mb-4">
              <li>Google: <a href="https://policies.google.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a></li>
              <li>Facebook: <a href="https://www.facebook.com/privacy/policy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">https://www.facebook.com/privacy/policy</a></li>
              <li>GitHub: <a href="https://docs.github.com/en/site-policy/privacy-policies/github-privacy-statement" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">https://docs.github.com/en/site-policy/privacy-policies/github-privacy-statement</a></li>
              <li>Apple: <a href="https://www.apple.com/legal/privacy/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">https://www.apple.com/legal/privacy/</a></li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4 mb-2 transition-colors duration-200">Hosting and infrastructure</h3>
            <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-200">
              This website uses Cloudflare, Inc. for content delivery, DNS, and security. Cloudflare
              processes IP addresses and technical request data (such as request headers and timestamps)
              to provide these services.
            </p>
            <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-200">
              Cloudflare’s privacy policy is available at: <a href="https://www.cloudflare.com/privacypolicy/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">https://www.cloudflare.com/privacypolicy/</a>
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 transition-colors duration-200">International Data Transfers</h2>
            <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-200">
              Your data may be processed in countries outside the EEA. When we transfer data
              internationally, we ensure appropriate safeguards are in place (standard contractual
              clauses, adequacy decisions, or other approved mechanisms).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 transition-colors duration-200">Children's Privacy</h2>
            <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-200">
              This service is not directed to individuals under 16 years of age. We do not
              knowingly collect personal data from children. If you believe we have collected
              data from a child, please contact us immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 transition-colors duration-200">Changes to This Policy</h2>
            <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-200">
              We may update this privacy policy from time to time. We will notify users of
              material changes by updating the "Last updated" date at the top of this page.
              Continued use of the service after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 transition-colors duration-200">Contact Us</h2>
            <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-200">
              For privacy-related questions, GDPR requests, or to exercise your rights:
            </p>
            <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-200">
              Email: <a href="mailto:info@onetimesecret.app" className="text-blue-600 hover:underline">info@onetimesecret.app</a>
            </p>
            <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-200">
              For GDPR complaints, you also have the right to lodge a complaint with your local
              data protection authority.
            </p>
          </section>
        </CardContent>
      </Card>
    </div>
  );
}
